<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import ConversationActionMenu from './ConversationActionMenu.vue';
import { useConversationNotificationMute } from 'dashboard/composables/useConversationNotificationMute';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const menuButton = ref(null);
const closeMenuWithFocus = () => {
  toggleDropdown(false);
  menuButton.value?.$el.focus();
};

const currentChat = computed(() => store.getters.getSelectedChat);

const {
  isMuted: notificationsMuted,
  isUpdating,
  toggleMute,
} = useConversationNotificationMute(computed(() => currentChat.value.id));

const actionMenuItems = computed(() => {
  const items = [
    {
      icon: notificationsMuted.value
        ? 'i-hugeicons-notification-02'
        : 'i-hugeicons-notification-off-01',
      label: t(
        notificationsMuted.value
          ? 'CONVERSATION.NOTIFICATION_MUTE.UNMUTE'
          : 'CONVERSATION.NOTIFICATION_MUTE.MUTE'
      ),
      description: t('CONVERSATION.NOTIFICATION_MUTE.DESCRIPTION'),
      action: 'notification_mute',
      disabled: isUpdating.value,
    },
  ];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-hugeicons-user-block-01',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      description: t('CONVERSATION.ACTION_MENU.BLOCK_DESCRIPTION'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-hugeicons-user-check-01',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      description: t('CONVERSATION.ACTION_MENU.UNBLOCK_DESCRIPTION'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-hugeicons-file-01',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    description: t('CONVERSATION.ACTION_MENU.TRANSCRIPT_DESCRIPTION'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const handleActionClick = async ({ action }) => {
  toggleDropdown(false);

  if (action === 'notification_mute') {
    try {
      await toggleMute();
      useAlert(
        t(
          notificationsMuted.value
            ? 'CONVERSATION.NOTIFICATION_MUTE.MUTED'
            : 'CONVERSATION.NOTIFICATION_MUTE.UNMUTED'
        )
      );
    } catch {
      useAlert(t('CONVERSATION.NOTIFICATION_MUTE.ERROR'));
    }
  } else if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
      @keydown.esc.stop="toggleDropdown(false)"
    >
      <ButtonV4
        ref="menuButton"
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        aria-haspopup="menu"
        :aria-label="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        :aria-expanded="showActionsDropdown"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-hugeicons-more-vertical"
        class="rounded-md border border-n-weak group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <ConversationActionMenu
        v-if="showActionsDropdown"
        :items="actionMenuItems"
        :title="t('CONVERSATION.HEADER.MORE_ACTIONS')"
        @close="closeMenuWithFocus"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
